import { useState } from 'react'
import { makeAssistantToolUI, type ToolCallMessagePartProps } from '@assistant-ui/react'

import { toMinor } from '../lib/money'

type EnterAccountResult = {
  name: string
  type: 'CHECKING' | 'SAVINGS'
  currency: string
  openingBalanceMinor: number
}

/**
 * One account, entered by hand, inside the conversation.
 *
 * A form rather than a series of questions. Three fields a person can see at once and
 * correct before committing beat three round trips, and turning a form into an
 * interrogation would be conversational theatre — the assistant is here to remove work,
 * not to dramatise it.
 *
 * The amount is converted to minor units here, where the currency is known. Sending a
 * decimal would put a float on the wire and lose exactness on the way.
 */
export const EnterAccountToolUI = makeAssistantToolUI<Record<string, never>, EnterAccountResult>({
  toolName: 'enterAccount',
  render: EnterAccountTool,
})

function EnterAccountTool({
  status,
  result,
  addResult,
}: ToolCallMessagePartProps<Record<string, never>, EnterAccountResult>) {
  const [name, setName] = useState('')
  const [type, setType] = useState<'CHECKING' | 'SAVINGS'>('CHECKING')
  const [balance, setBalance] = useState('')
  const waiting = status.type === 'requires-action'
  const currency = 'USD'

  if (result) {
    return <p className="aui-note">Added {result.name}.</p>
  }

  const minor = toMinor(balance, currency)
  const valid = name.trim().length > 0 && minor !== null

  return (
    <form
      className="aui-form"
      onSubmit={(event) => {
        event.preventDefault()
        if (!valid || minor === null) return
        addResult({ name: name.trim(), type, currency, openingBalanceMinor: minor })
      }}
    >
      <label className="aui-field">
        <span>Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Everyday Checking"
          disabled={!waiting}
        />
      </label>
      <label className="aui-field">
        <span>Type</span>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as 'CHECKING' | 'SAVINGS')}
          disabled={!waiting}
        >
          <option value="CHECKING">Checking</option>
          <option value="SAVINGS">Savings</option>
        </select>
      </label>
      <label className="aui-field">
        <span>Balance today (USD)</span>
        <input
          value={balance}
          onChange={(e) => setBalance(e.target.value)}
          placeholder="what your bank says right now"
          inputMode="decimal"
          disabled={!waiting}
        />
      </label>
      <div className="aui-action">
        <button type="submit" className="btn-primary" disabled={!valid || !waiting}>
          Add account
        </button>
      </div>
    </form>
  )
}
